import { addDay, addMonth, dayEnd, dayStart, diffMinutes, format, isAfter, sameDay, tzDate } from '@formkit/tempo';

export type DateRange = {
    fromDate: string;
    toDate: string;
    offsetDates: string;
};

export const getTodayDate = () => new Date();

export const getDateOrToday = (date?: Date): Date => date ?? new Date();

export const dateDiffInDays = (daysPlus: number, date: Date): number => {
    const today = new Date();
    const adjustedDate = today.getTime() + daysPlus * 24 * 60 * 60 * 1000;
    const diffInMilliseconds = adjustedDate - date.getTime();
    const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
    return Math.round(diffInDays);
};

export const isSameDay = (date1?: Date, date2?: Date): boolean =>
    date1 instanceof Date && date2 instanceof Date && sameDay(date1, date2);

export const getDateOrUndefined = (date?: Date | string): Date | undefined => (date ? new Date(date) : undefined);

export const transformDateToISO = (date: Date): string =>
    format({
        date,
        format: 'YYYY-MM-DDTHH:mm:ssZ',
    });

export const formatDateAsIsoDate = (date: Date): string =>
    format({
        date,
        format: 'YYYY-MM-DD',
    });

export const startOfToday = (): Date => dayStart(new Date());

export const formatLocaleDate = (date: string | Date, language: string) => {
    const newDate = new Date(date);
    const formatter = new Intl.DateTimeFormat(language.replace('_', '-'), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    return formatter.format(newDate);
};

export function formatLocaleTime(date: string | Date, language: string) {
    const newDate = new Date(date);
    const formatter = new Intl.DateTimeFormat(language.replace('_', '-'), {
        hour: '2-digit',
        minute: '2-digit',
    });
    return formatter.format(newDate);
}

export const formatDateTime = (date: string | Date, language: string) => {
    const newDate = new Date(date);
    const formatter = new Intl.DateTimeFormat(language.replace('_', '-'), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return formatter.format(newDate).replace(',', '');
};

export const startDate = (date: Date | string): Date => dayStart(date);
export const endDate = (date: Date | string): Date => dayEnd(date);
// you can add days as negative number to subtract days
export const addDays = (date: Date | string, days: number): Date => addDay(date, days);
export const addMonths = (date: Date | string, months: number): Date => addMonth(date, months);

export const toTimeZoneDate = (date: Date, timeZone: string): Date => tzDate(date, timeZone);

type PickedDay = Date | number | string;

const startOfZonedDay = (day: PickedDay, timeZone: string, daysAhead = 0): string =>
    transformDateToISO(toTimeZoneDate(startDate(addDays(new Date(day), daysAhead)), timeZone));

export const toTenantDayWindow = (
    firstDay: PickedDay | null | undefined,
    lastDay: PickedDay | null | undefined,
    timeZone: string
): { from?: string; to?: string } => ({
    from: firstDay ? startOfZonedDay(firstDay, timeZone) : undefined,
    to: lastDay ? startOfZonedDay(lastDay, timeZone, 1) : undefined,
});

export const isInvertedRange = (from?: PickedDay | null, to?: PickedDay | null): boolean =>
    !!from && !!to && new Date(from).getTime() > new Date(to).getTime();

export const formatDateTimeOrNow = (date: string | Date, language: string, nowLabel: string): string => {
    const now = new Date();
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) {
        return String(date);
    }
    if (isAfter(parsed, now)) {
        return formatDateTime(date, language);
    }
    if (diffMinutes(now, parsed) < 5) {
        return nowLabel;
    }
    return formatDateTime(parsed, language);
};
